import { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useIsMobile from "../hooks/useIsMobile";
import api from "../api/client";

/* ---------- helpers ---------- */

function today() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateHeader(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

/* ---------- Multi-select dropdown ---------- */

function MultiSelect({ label, items, selected, onChange, onSelectAll, onClearAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayText =
    selected.length === 0
      ? `All ${label}`
      : selected.length === items.length
      ? `All ${label}`
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={ref}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <button
        onClick={() => setOpen((o) => !o)}
        className="border rounded px-3 py-1.5 text-sm bg-white min-w-[220px] text-left flex items-center justify-between"
      >
        <span className="truncate">{displayText}</span>
        <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-72 bg-white border rounded shadow-lg max-h-64 overflow-auto">
          <div className="flex gap-2 px-3 py-2 border-b sticky top-0 bg-white">
            <button onClick={onSelectAll} className="text-xs text-blue-600 hover:underline">Select All</button>
            <button onClick={onClearAll} className="text-xs text-blue-600 hover:underline">Clear All</button>
          </div>
          {items.map((name) => (
            <label key={name} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={selected.includes(name)}
                onChange={() => onChange(name)}
                className="rounded"
              />
              <span className="truncate">{name}</span>
            </label>
          ))}
          {items.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">None available</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Sort icon ---------- */

function SortIcon({ direction }) {
  if (!direction) {
    return (
      <svg className="w-3 h-3 ml-1 inline opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return direction === "asc" ? (
    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/* ---------- Report table ---------- */

function ReportTable({ label, data }) {
  const { dates, rows, grand_totals } = data;
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (!sortKey) return 0;
    if (sortKey === "name") {
      const va = (a.name || "").toLowerCase();
      const vb = (b.name || "").toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    const va = sortKey === "total" ? a.total : (a.counts[sortKey] || 0);
    const vb = sortKey === "total" ? b.total : (b.counts[sortKey] || 0);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-8 text-center text-gray-400">No data for the selected filters.</div>
      </div>
    );
  }

  const thClass = "px-3 py-2 font-semibold text-gray-700 min-w-[80px] cursor-pointer select-none hover:bg-gray-200 transition-colors";

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th
                onClick={() => handleSort("name")}
                className="px-3 py-2 text-left font-semibold text-gray-700 sticky left-0 bg-gray-100 z-10 min-w-[200px] cursor-pointer select-none hover:bg-gray-200 transition-colors"
              >
                {label}
                <SortIcon direction={sortKey === "name" ? sortDir : null} />
              </th>
              {dates.map((d) => (
                <th key={d} onClick={() => handleSort(d)} className={`${thClass} text-center`}>
                  {formatDateHeader(d)}
                  <SortIcon direction={sortKey === d ? sortDir : null} />
                </th>
              ))}
              <th
                onClick={() => handleSort("total")}
                className="px-3 py-2 text-center font-bold text-gray-800 bg-blue-50 min-w-[80px] cursor-pointer select-none hover:bg-blue-100 transition-colors"
              >
                Total
                <SortIcon direction={sortKey === "total" ? sortDir : null} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => (
              <tr key={row.name} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                <td className="px-3 py-2 font-medium text-gray-800 sticky left-0 z-10" style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}>
                  {row.name}
                </td>
                {dates.map((d) => (
                  <td key={d} className="px-3 py-2 text-center text-gray-700">
                    {row.counts[d] || 0}
                  </td>
                ))}
                <td className="px-3 py-2 text-center font-bold text-gray-800 bg-blue-50">
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-blue-50 font-bold">
              <td className="px-3 py-2 text-gray-800 sticky left-0 bg-blue-50 z-10">Grand Total</td>
              {dates.map((d) => (
                <td key={d} className="px-3 py-2 text-center text-gray-800">
                  {grand_totals[d] || 0}
                </td>
              ))}
              <td className="px-3 py-2 text-center text-gray-900 bg-blue-100">
                {grand_totals.total || 0}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ---------- Main page ---------- */

export default function CityOutputPage() {
  const isMobile = useIsMobile();
  /* filters */
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());

  const [allAgencies, setAllAgencies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);

  const [availableCities, setAvailableCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);

  /* data */
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  /* fetch all agencies once on mount */
  useEffect(() => {
    api.get("/reports/agencies").then(({ data }) => setAllAgencies(data)).catch(() => {});
  }, []);

  /* fetch cities whenever agency selection changes */
  useEffect(() => {
    const params = {};
    if (selectedAgencies.length > 0) {
      params.agencies = selectedAgencies.join(",");
    }
    api
      .get("/reports/cities", { params })
      .then(({ data }) => {
        setAvailableCities(data);
        // Clear city selections that no longer exist in the new list
        setSelectedCities((prev) => prev.filter((c) => data.includes(c)));
      })
      .catch(() => {});
  }, [selectedAgencies]);

  /*
   * Determine grouping mode:
   * - Cities selected WITHOUT agency filter → group_by_agency = true  ("City - Agency" rows)
   * - Otherwise → group_by_agency = false  ("City" rows)
   */
  const groupByAgency = selectedCities.length > 0 && selectedAgencies.length === 0;

  /* fetch report data */
  const fetchReport = useCallback(async () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo, group_by_agency: groupByAgency };
    if (selectedAgencies.length > 0) {
      params.agencies = selectedAgencies.join(",");
    }
    if (selectedCities.length > 0) {
      params.cities = selectedCities.join(",");
    }
    try {
      const { data } = await api.get("/reports/city-output", { params });
      setReportData(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedAgencies, selectedCities, groupByAgency]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  /* toggle helpers */
  const toggleAgency = (name) =>
    setSelectedAgencies((prev) => (prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name]));
  const toggleCity = (name) =>
    setSelectedCities((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));

  const columnLabel = groupByAgency ? "City - Agency" : "City";

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        <div className="flex items-center gap-3 mb-4">
          <a href="/reports" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </a>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Output Per City</h1>

        {/* ---- Filters ---- */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>

          <MultiSelect
            label="Agency"
            items={allAgencies}
            selected={selectedAgencies}
            onChange={toggleAgency}
            onSelectAll={() => setSelectedAgencies([...allAgencies])}
            onClearAll={() => setSelectedAgencies([])}
          />

          <MultiSelect
            label="City"
            items={availableCities}
            selected={selectedCities}
            onChange={toggleCity}
            onSelectAll={() => setSelectedCities([...availableCities])}
            onClearAll={() => setSelectedCities([])}
          />
        </div>

        {loading && <div className="text-center text-gray-500 py-8">Loading report…</div>}

        {!loading && reportData && <ReportTable label={columnLabel} data={reportData} />}
      </div>
    </div>
  );
}
